/**
 * The route-level error boundary.
 *
 * FRONTEND_DESIGN §6.4: "Use route-level error boundaries so an import preview
 * error cannot destroy an active response." Nothing has an active response yet
 * — but the boundary has to be attached to the route tree from the start,
 * because the failure it prevents is a whole-app unmount, and by Phase 1 the
 * thing that would be unmounted is a response the learner has typed and not
 * yet submitted.
 *
 * The failure-to-sentence mapping lives in `failure-notice.tsx` so that this
 * boundary and every data-fetching screen give the same answer to the same
 * failure. What is specific to a boundary is the router's own error type: a
 * 404 from React Router is a bad address, not a server fault.
 */

import { isRouteErrorResponse, useRouteError } from 'react-router';
import { useMessages } from '../i18n/locale.js';
import { defineMessages } from '../i18n/messages.js';
import { FailureNotice } from './failure-notice.js';

const messages = defineMessages({
  en: {
    title: 'That page does not exist',
    body: 'The address is not one DaVinci knows. Use the navigation above to get back.',
  },
  he: {
    title: 'הדף הזה לא קיים',
    body: 'הכתובת אינה מוכרת ל-DaVinci. השתמש בניווט שלמעלה כדי לחזור.',
  },
});

export function RouteError(): React.JSX.Element {
  const error = useRouteError();
  const t = useMessages(messages);

  if (isRouteErrorResponse(error)) {
    return (
      <div className="notice notice-screen" role="alert">
        <h2>{t.title}</h2>
        <p>{t.body}</p>
      </div>
    );
  }

  return <FailureNotice error={error} onRetry={() => window.location.reload()} />;
}
